import {
  ProcessingContentCenter,
  ProcessingContentSidebar,
} from "@/components/feature/QuestionnaireProcessingView/QuestionnaireProcessingView"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useOnboardingQuestionnaireData } from "@/features/onboarding"
import { useQuestionnairePolling } from "@/hooks/useQuestionnairePolling/useQuestionnairePolling"
import { ErrorPage } from "@/pages/ErrorPage"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import {
  getFileExtension,
  isExcelFileName,
  isPdfFileName,
} from "@/utils/fileUtils"
import { getRouteByStatus } from "@/utils/questionnaire/questionnaireStatus"
import { useCallback, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router"

export const QuestionnaireProcessingPage: React.FC = () => {
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const navigate = useNavigate()
  const hasNavigatedRef = useRef(false)
  const excelIntelligenceEnabled = useFeatureFlag("EXCEL_INTELLIGENCE")
  const pdfIntelligenceEnabled = useFeatureFlag("PDF_INTELLIGENCE")

  const {
    data: questionnaire,
    isFetched: isQuestionnaireFetched,
    error: questionnaireError,
  } = useOnboardingQuestionnaireData(questionnaireId)

  const navigateToQuestionsByFileType = useCallback(() => {
    if (!questionnaireId || !questionnaire) {
      return
    }

    hasNavigatedRef.current = true

    if (pdfIntelligenceEnabled && isPdfFileName(questionnaire.fileName)) {
      navigate(`/questionnaire/${questionnaireId}/pdf-questions`, {
        replace: true,
      })
    } else if (
      excelIntelligenceEnabled &&
      isExcelFileName(questionnaire.fileName) &&
      // Without cell positions the backend serves a PDF summary, not the workbook.
      questionnaire.hasPositionData
    ) {
      navigate(`/questionnaire/${questionnaireId}/excel-questions`, {
        replace: true,
      })
    } else {
      navigate(`/questionnaire/${questionnaireId}/questions`, { replace: true })
    }

    const fileExtension = getFileExtension(questionnaire.fileName)
    trackEvent(AnalyticsEvent.QUESTIONNAIRE_UPLOADED, {
      file_type: fileExtension,
      questionnaire_id: questionnaire.id,
    })
  }, [
    navigate,
    questionnaire,
    questionnaireId,
    pdfIntelligenceEnabled,
    excelIntelligenceEnabled,
  ])

  const navigateToFailedQuestions = useCallback(() => {
    if (!questionnaireId || hasNavigatedRef.current) {
      return
    }

    navigateToQuestionsByFileType()
  }, [navigateToQuestionsByFileType, questionnaireId])

  const shouldPoll =
    !!questionnaireId &&
    !!questionnaire &&
    !isQuestionnaireNotFound(questionnaireError) &&
    (questionnaire?.status === QuestionnaireStatus.New ||
      questionnaire?.status === QuestionnaireStatus.Extracting)

  // No onExtracted: the detail held here is still the pre-extraction one. Polling
  // refetches it on extraction, and the effect below routes once it says extracted.
  const { pollingError } = useQuestionnairePolling({
    questionnaireId,
    enabled: shouldPoll,
    onFailed: (error) => {
      if (!isQuestionnaireNotFound(error)) navigateToFailedQuestions()
    },
  })

  useEffect(() => {
    if (!isQuestionnaireFetched || !questionnaire || hasNavigatedRef.current) {
      return
    }

    if (questionnaire.status === QuestionnaireStatus.Extracted) {
      navigateToQuestionsByFileType()
      return
    }

    if (questionnaire.status === QuestionnaireStatus.Failed) {
      navigateToFailedQuestions()
      return
    }

    if (
      questionnaire.status !== QuestionnaireStatus.New &&
      questionnaire.status !== QuestionnaireStatus.Extracting
    ) {
      hasNavigatedRef.current = true
      navigate(
        getRouteByStatus(
          questionnaire.id,
          questionnaire.status,
          questionnaire.fileName,
          questionnaire.hasPositionData,
          undefined,
          {
            EXCEL_INTELLIGENCE: excelIntelligenceEnabled,
            PDF_INTELLIGENCE: pdfIntelligenceEnabled,
          },
        ),
        { replace: true },
      )
    }
  }, [
    isQuestionnaireFetched,
    questionnaire,
    navigate,
    navigateToFailedQuestions,
    navigateToQuestionsByFileType,
    excelIntelligenceEnabled,
    pdfIntelligenceEnabled,
  ])

  if (!questionnaireId) {
    return null
  }

  if (
    isQuestionnaireNotFound(questionnaireError) ||
    isQuestionnaireNotFound(pollingError)
  ) {
    return <ErrorPage variant="not-found" withLayout />
  }

  return (
    <MainLayout
      contentCenter={
        <ProcessingContentCenter questionnaireId={questionnaireId} />
      }
      contentRight={
        <ProcessingContentSidebar fileName={questionnaire?.fileName || ""} />
      }
    />
  )
}
